import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plug, RefreshCw, Trash2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';

const STATUS_LABELS = {
  NOT_CONFIGURED: 'Non configuré',
  CONFIGURED: 'Configuré',
  CONNECTED: 'Connecté',
  ERROR: 'Erreur',
};

/**
 * Super Admin — onboarding credentials Boxtal (B2.1).
 * Pas de readback secret après enregistrement.
 */
export default function BoxtalShippingProviderPanel({ commercantId }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [environment, setEnvironment] = useState('test');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');

  const loadStatus = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      const data = await apiService.getBoxtalShippingProviderStatus(commercantId);
      setStatus(data?.status || null);
      if (data?.status?.environment) {
        setEnvironment(data.status.environment);
      }
    } catch (err) {
      toast.error(err?.message || 'Impossible de charger le statut Boxtal');
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await apiService.putBoxtalShippingProviderCredentials(commercantId, {
        environment,
        accessKey: accessKey.trim(),
        secretKey: secretKey.trim(),
      });
      setStatus(data?.status || null);
      setAccessKey('');
      setSecretKey('');
      setFormOpen(false);
      toast.success('Identifiants Boxtal enregistrés');
    } catch (err) {
      toast.error(err?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const data = await apiService.testBoxtalShippingProviderConnection(commercantId);
      setStatus(data?.status || status);
      if (data?.ok) {
        toast.success('Connexion Boxtal v3 réussie');
      } else {
        toast.error(data?.error?.message || 'Test de connexion échoué');
      }
    } catch (err) {
      toast.error(err?.message || 'Test de connexion impossible');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer les identifiants Boxtal pour ce commerçant ?')) return;
    setBusy(true);
    try {
      const data = await apiService.deleteBoxtalShippingProviderCredentials(commercantId);
      setStatus(data?.status || null);
      toast.success('Identifiants supprimés');
    } catch (err) {
      toast.error(err?.message || 'Suppression impossible');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement intégration Boxtal…
      </div>
    );
  }

  const st = status?.status || 'NOT_CONFIGURED';

  return (
    <div className="mb-8 rounded-xl border border-indigo-100 bg-indigo-50/40 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Intégration transporteur
          </p>
          <h3 className="text-lg font-semibold text-gray-900">Boxtal</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configuration technique Super Admin — le commerçant ne gère pas les clés API.
          </p>
        </div>
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
            st === 'CONNECTED'
              ? 'bg-green-100 text-green-800'
              : st === 'ERROR'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
          }`}
        >
          {STATUS_LABELS[st] || st}
        </span>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-gray-500">Environnement</dt>
          <dd className="font-medium">{status?.environment || '—'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Access key</dt>
          <dd className="font-medium font-mono">{status?.accessKeyHint || '—'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Secret</dt>
          <dd className="font-medium">{status?.secretConfigured ? 'Configuré' : 'Non configuré'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Dernière vérification</dt>
          <dd className="font-medium">
            {status?.lastVerifiedAt
              ? new Date(status.lastVerifiedAt).toLocaleString('fr-FR')
              : '—'}
          </dd>
        </div>
        {status?.lastErrorMessage ? (
          <div className="sm:col-span-2">
            <dt className="text-gray-500">Dernière erreur</dt>
            <dd className="font-medium text-red-700">{status.lastErrorMessage}</dd>
          </div>
        ) : null}
      </dl>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => setFormOpen((v) => !v)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {formOpen ? 'Fermer le formulaire' : 'Configurer / remplacer'}
        </button>
        <button
          type="button"
          disabled={busy || !status?.secretConfigured}
          onClick={handleTest}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Plug className="h-4 w-4" />
          Tester la connexion
        </button>
        <button
          type="button"
          disabled={busy || st === 'NOT_CONFIGURED'}
          onClick={handleDelete}
          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
          Supprimer
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={loadStatus}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {formOpen ? (
        <form onSubmit={handleSaveCredentials} className="border-t border-indigo-100 pt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Environnement</label>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="test">Test (sandbox)</option>
              <option value="production">Production</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access key</label>
            <input
              type="password"
              autoComplete="off"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Secret key</label>
            <input
              type="password"
              autoComplete="new-password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            Enregistrer (remplace l’existant)
          </button>
        </form>
      ) : null}
    </div>
  );
}
